import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {DisposalIntent, INTENT_OPTIONS} from '../support/itemLifecycle';
import AuthImage from './AuthImage';
import Spinner from './Spinner';
import Dialog from './Dialog';
import {SearchIcon, TrashIcon} from './icons';

/**
 * Диалог «Одно пришло — одно ушло» (one-in-one-out, FR-3): после добавления новой вещи
 * предлагает выбрать старую на выбытие — кандидаты с объяснимостью
 * (`GET /disposal/suggestions`) плюс поиск по своим вещам (`GET /clothing/items?search=`).
 *
 * Primary «Решить расстаться» зовёт существующий обратимый контур
 * `POST /clothing/items/{id}/dispose` (намерение выбирается селектом, дефолт — «Выбросить»);
 * «Пропустить» закрывает без изменений. Пока кандидаты грузятся, ничего не рендерится;
 * при `activeItemCount < 2` диалог закрывается сам (FR-4).
 */

interface CandidateDto {
    id: string;
    name: string;
    brandName: string | null;
    categoryName: string | null;
    primaryPhotoUrl: string | null;
    totalWears: number;
    lastWornAt: string | null;
    reasons: string[];
}

interface SuggestionsDto {
    activeItemCount: number;
    candidates: CandidateDto[];
}

interface SearchItemDto {
    id: string;
    name: string;
    categoryName?: string | null;
    firstPhotoUrl?: string | null;
    inTrashBin?: boolean | null;
    thrownAway?: boolean | null;
}

interface OneInOneOutDialogProps {
    /** Только что добавленная вещь — исключается из кандидатов и поиска. */
    addedItemId: string;
    addedItemName: string;
    /** Категория новой вещи; если не задана, backend выводит её из excludeItemId. */
    categoryId?: string | null;
    /** Владелец гардероба (общий гардероб); null/undefined — свой. */
    ownerId?: string | null;
    onClose: () => void;
    /** Успешное выбытие выбранной вещи (для обновления списков родителя). */
    onDisposed?: (disposedItemId: string) => void;
}

/** Кандидат из поиска приводится к минимальному виду карточки (без причин скоринга). */
const searchItemToCandidate = (item: SearchItemDto): CandidateDto => ({
    id: item.id,
    name: item.name,
    brandName: null,
    categoryName: item.categoryName ?? null,
    primaryPhotoUrl: item.firstPhotoUrl ?? null,
    totalWears: 0,
    lastWornAt: null,
    reasons: [],
});

const OneInOneOutDialog: React.FC<OneInOneOutDialogProps> = (
    {addedItemId, addedItemName, categoryId, ownerId, onClose, onDisposed}
) => {
    const [suggestions, setSuggestions] = useState<SuggestionsDto | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState<CandidateDto[] | null>(null);
    const [searching, setSearching] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [intent, setIntent] = useState<DisposalIntent>('DISPOSED');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const params: Record<string, string> = {excludeItemId: addedItemId};
                if (categoryId) params.categoryId = categoryId;
                if (ownerId) params.ownerId = ownerId;
                const res = await api.get<SuggestionsDto>('/disposal/suggestions', {params});
                if (cancelled) return;
                if ((res.data?.activeItemCount ?? 0) < 2) {
                    // FR-4: в гардеробе меньше двух активных вещей — диалог не показываем
                    onClose();
                    return;
                }
                setSuggestions(res.data);
            } catch {
                if (!cancelled) setLoadError(true);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addedItemId, categoryId, ownerId]);

    // Поиск по своим вещам с дебаунсом; добавленная вещь и вещи из корзины исключаются
    useEffect(() => {
        const trimmed = query.trim();
        if (trimmed.length < 2) {
            setSearchResults(null);
            setSearching(false);
            return;
        }
        setSearching(true);
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                const api = await createAxiosInstance();
                const params: Record<string, string | number> = {search: trimmed, size: 10};
                if (ownerId) params.ownerId = ownerId;
                const res = await api.get<{content?: SearchItemDto[]}>('/clothing/items', {params});
                if (cancelled) return;
                const found = (res.data?.content ?? [])
                    .filter(i => i.id !== addedItemId && !i.inTrashBin && !i.thrownAway)
                    .map(searchItemToCandidate);
                setSearchResults(found);
            } catch {
                if (!cancelled) setSearchResults([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [query, addedItemId, ownerId]);

    const dispose = useCallback(async () => {
        if (!selectedId || submitting) return;
        setSubmitting(true);
        setSubmitError(false);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/items/${selectedId}/dispose`, {intent});
            onDisposed?.(selectedId);
            onClose();
        } catch {
            setSubmitError(true);
            setSubmitting(false);
        }
    }, [selectedId, submitting, intent, onDisposed, onClose]);

    // Пока suggestions грузятся — ничего не рендерим (не «моргаем» модалкой, FR-4)
    if (!suggestions && !loadError) {
        return null;
    }

    const shownCandidates = searchResults ?? suggestions?.candidates ?? [];

    const renderCandidate = (c: CandidateDto) => {
        const selected = selectedId === c.id;
        return (
            <li key={c.id}>
                <button
                    type="button"
                    onClick={() => setSelectedId(selected ? null : c.id)}
                    aria-pressed={selected}
                    data-testid={`oio-candidate-${c.id}`}
                    className={`w-full min-h-[44px] text-left rounded-xl border p-3 flex gap-3 items-start transition-colors ${
                        selected
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                            : 'border-gray-200 dark:border-ink-700 hover:border-brand-400'
                    }`}
                >
                    <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-700 flex items-center justify-center">
                        {c.primaryPhotoUrl ? (
                            <AuthImage imageKey={c.primaryPhotoUrl} alt={c.name} variant="thumbnail"
                                       className="w-full h-full object-cover"/>
                        ) : (
                            <TrashIcon className="w-5 h-5 text-gray-300 dark:text-ink-500"/>
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex justify-between gap-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                {c.name}
                                {c.brandName && <span className="text-gray-500"> · {c.brandName}</span>}
                            </span>
                            {c.categoryName && (
                                <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                                    {c.categoryName}
                                </span>
                            )}
                        </div>
                        {c.reasons.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                                {c.reasons.map((r, idx) => (
                                    <span key={idx}
                                          className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-ink-700 text-xs text-gray-700 dark:text-gray-200">
                                        {r}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </button>
            </li>
        );
    };

    return (
        <Dialog
            open
            onClose={onClose}
            title="Одно пришло — одно ушло"
            busy={submitting}
            data-testid="one-in-one-out-dialog"
            panelClassName="bg-white dark:bg-ink-800 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col outline-none"
            headerClassName="flex items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-ink-700"
            bodyClassName="p-4 space-y-4 overflow-y-auto"
            footerClassName="flex gap-2 p-4 border-t border-gray-200 dark:border-ink-700"
            footer={
                <>
                    <button
                        type="button"
                        onClick={dispose}
                        disabled={!selectedId || submitting || loadError}
                        className="flex-1 inline-flex min-h-[44px] items-center justify-center gap-2 px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                        data-testid="oio-dispose-submit"
                    >
                        <TrashIcon size={18}/> {submitting ? 'Сохраняем…' : 'Решить расстаться'}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        className="min-h-[44px] px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-gray-600 dark:text-gray-300 hover:border-brand-500"
                        data-testid="oio-skip"
                    >
                        Пропустить
                    </button>
                </>
            }
        >
                    {loadError ? (
                        <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="oio-load-error">
                            Не удалось загрузить кандидатов на выбытие. Закройте диалог и попробуйте позже.
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                В гардеробе появилась «{addedItemName}». Выберите, с чем расстаться
                                взамен, — или пропустите шаг.
                            </p>

                            <label className="relative block">
                                <span className="sr-only">Поиск по своим вещам</span>
                                <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                                <input
                                    type="text"
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Найти другую вещь…"
                                    aria-label="Поиск по своим вещам"
                                    className="w-full min-h-[44px] pl-9 pr-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-gray-900 dark:text-gray-100"
                                />
                            </label>

                            {searching ? (
                                <div className="flex justify-center py-6"><Spinner/></div>
                            ) : shownCandidates.length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="oio-empty">
                                    {searchResults ? 'Ничего не найдено.' : 'Подходящих кандидатов нет — воспользуйтесь поиском.'}
                                </p>
                            ) : (
                                <ul className="space-y-2" data-testid="oio-candidates">
                                    {shownCandidates.map(renderCandidate)}
                                </ul>
                            )}

                            <label className="block">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Что сделать с вещью
                                </span>
                                <select
                                    value={intent}
                                    onChange={e => setIntent(e.target.value as DisposalIntent)}
                                    aria-label="Намерение выбытия"
                                    className="mt-1 w-full min-h-[44px] px-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-900 text-gray-900 dark:text-gray-100"
                                >
                                    {INTENT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </label>

                            {submitError && (
                                <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="oio-submit-error">
                                    Не удалось записать решение. Попробуйте ещё раз.
                                </p>
                            )}
                        </>
                    )}
        </Dialog>
    );
};

export default OneInOneOutDialog;
